import { Alert, Snackbar, Stack } from "@mui/material";
import { useCallback, useState, type ReactNode } from "react";
import {
  NotificationContext,
  type Notification,
  type NotificationSeverity,
} from "./NotificationContext";

const DEFAULT_AUTO_HIDE: Record<NotificationSeverity, number | null> = {
  success: 3000,
  info: 4000,
  warning: 6000,
  error: 6000,
};

let notificationIdCounter = 0;

export function NotificationProvider({ children }: { children: ReactNode }) {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const showNotification = useCallback(
    (
      message: string,
      severity: NotificationSeverity = "error",
      autoHideDuration?: number | null,
    ): string => {
      const id = `notification-${++notificationIdCounter}`;
      const notification: Notification = {
        id,
        message,
        severity,
        autoHideDuration:
          autoHideDuration !== undefined
            ? autoHideDuration
            : DEFAULT_AUTO_HIDE[severity],
      };

      setNotifications((prev) => {
        // Deduplicate: if an identical message+severity is already showing, skip
        if (
          prev.some((n) => n.message === message && n.severity === severity)
        ) {
          return prev;
        }
        return [...prev, notification];
      });

      return id;
    },
    [],
  );

  const dismissNotification = useCallback((id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  const handleClose = (id: string) => () => {
    dismissNotification(id);
  };

  return (
    <NotificationContext value={{ showNotification, dismissNotification }}>
      {children}
      <Stack
        spacing={1}
        sx={{
          position: "fixed",
          bottom: 16,
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: (theme) => theme.zIndex.snackbar,
          maxWidth: "90vw",
          width: "auto",
          minWidth: 300,
        }}
      >
        {notifications.map((notification) => (
          <Snackbar
            key={notification.id}
            open
            autoHideDuration={notification.autoHideDuration ?? undefined}
            onClose={handleClose(notification.id)}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            sx={{ position: "static", transform: "none" }}
          >
            <Alert
              onClose={handleClose(notification.id)}
              severity={notification.severity}
              variant="filled"
              sx={{ width: "100%" }}
            >
              {notification.message}
            </Alert>
          </Snackbar>
        ))}
      </Stack>
    </NotificationContext>
  );
}
